import {useCallback} from 'react';

import styled from 'styled-components';

import {attachModal} from '~/shared/components/ReduxModal';
import {headingXS} from '~/shared/theme/typography';
import {media} from '~/shared/theme/media';
import {getLocalizationService} from '~/shared/services/localisationService';
import AddCreditCard, {MinimalBlockWrapper} from '~/shared/components/AddCreditCard';
import {AddCreditCardModes} from '~/shared/consts/checkoutConsts';
import {trackEvent} from '~/shared/services/analytics';
import {PieCloseModalButton} from '~/shared/components/Modals/styled';
import {flipOnLTR} from '~/shared/theme/utils';

const Root = styled.div`
  position: relative;
  ${media.minLargeMobile`
    ${MinimalBlockWrapper} {
      width: 500px;
    }
  `}

  &:first-child {
    overflow-y: hidden;
    background-color: ${({theme}) => theme.colors.surface};
    width: 100%;
  }
`;

const StyledPieCloseModalButton = styled(PieCloseModalButton)`
  top: 16px;
  ${flipOnLTR`
    left: 16px;
  `}
`;

const Header = styled.p`
  ${headingXS}
  color: ${({theme}) => theme.colors.charcoal80};
  padding: 16px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  text-align: center;
`;

type AddPaymentMethodModalProps = {
  closeModal: () => void;
  args: {
    onSuccess?: (() => void) | (() => Promise<void>);
    onFailure?: (e: unknown) => void;
    AddCreditCardComponent: typeof AddCreditCard;
    pageSource?: string;
  };
};

export const AddPaymentMethodModal = ({closeModal, args}: AddPaymentMethodModalProps) => {
  const {AddCreditCardComponent, pageSource, onSuccess, onFailure} = args;
  const {t} = getLocalizationService();
  const onSuccessHandler = useCallback(async () => {
    trackEvent('addPaymentMethod_successful', {
      linkType: 'credit_card',
      pageSource,
    });
    closeModal();
    if (onSuccess) {
      await onSuccess();
    }
  }, [pageSource, onSuccess, closeModal]);

  return (
    <Root>
      <StyledPieCloseModalButton onClick={closeModal} />
      <Header id="modal-title">
        {t('add_a_payment_method')}
      </Header>
      <AddCreditCardComponent
        mode={AddCreditCardModes.MINIMAL}
        onSuccess={onSuccessHandler}
        onFailure={onFailure}
        minimalWrapper
      />
    </Root>
  );
};

AddPaymentMethodModal.modalType = 'autoWidth';
AddPaymentMethodModal.hideCloseButton = true;
attachModal('addPaymentMethodModal', AddPaymentMethodModal);
